import React, { useState, useRef, useEffect } from 'react';
import { Card, CardBody, Table, Badge } from 'reactstrap';
import './RaceTeeDistributionTable.scss';

const RaceTeeDistributionTable = ({ data, loading }) => {
    const [hoveredCell, setHoveredCell] = useState(null);
    const tableRef = useRef(null);
    
    // Function to truncate club names if they're too long
    const truncateClubName = (clubName, maxLength = 12) => {
        if (clubName.length <= maxLength) return clubName;
        return clubName.substring(0, maxLength) + '...';
    };
    
    // Function to handle cell hover
    const handleCellHover = (rowIndex, colIndex) => {
        setHoveredCell({ row: rowIndex, col: colIndex });
    };
    
    // Function to handle cell leave
    const handleCellLeave = () => {
        setHoveredCell(null);
    };
    
    // Function to get highlight class for a cell
    const getHighlightClass = (rowIndex, colIndex) => {
        if (!hoveredCell) return '';
        
        if (hoveredCell.row === rowIndex && hoveredCell.col === colIndex) {
            return 'cell-highlighted'; // Hovered cell
        } else if (hoveredCell.row === rowIndex) {
            return 'row-highlighted'; // Same row
        } else if (hoveredCell.col === colIndex) {
            return 'column-highlighted'; // Same column
        }
        
        return '';
    };
    
    // Debug logging
    useEffect(() => {
        if (hoveredCell) {
            console.log('Hovered cell:', hoveredCell);
        }
    }, [hoveredCell]);

    if (loading) {
        return (
            <Card className="mb-0">
                <CardBody>
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </CardBody>
            </Card>
        );
    }

    if (!data || !data.distribution || Object.keys(data.distribution).length === 0) {
        return (
            <Card className="mb-0">
                <CardBody>
                    <h3 className="text-center mb-4">Race Tee Distribution by Club</h3>
                    <div className="text-center text-muted">
                        <p>No race tee distribution data available.</p>
                        <small>This could be because there are no active events, no race kits, or no clubs defined.</small>
                    </div>
                </CardBody>
            </Card>
        );
    }

    const { distribution, clubs, teeSizes } = data;
    // const allColumns = [...clubs, 'No Club', 'Total', 'Remaining'];

    return (
        <Card className="mb-0">
            <CardBody>
                <h3 className="text-center mb-4">Race Tee Distribution by Club</h3>
                
                <div className="table-responsive">
                    <Table ref={tableRef} className="table-bordered table-hover race-tee-table">
                        <thead>
                            <tr>
                                <th className="sticky-column">Tee Size</th>
                                {clubs.map((club, index) => (
                                    <th key={index} className="club-column">
                                        <div className="club-header">
                                            <div className="club-name-wrapper">
                                                <span className="club-name" title={club}>
                                                    {truncateClubName(club)}
                                                </span>
                                            </div>
                                        </div>
                                    </th>
                                ))}
                                <th className="no-club-column">
                                    <div className="club-header">
                                        <div className="club-name-wrapper">
                                            <span className="club-name" title="No Club">
                                                {truncateClubName("No Club")}
                                            </span>
                                        </div>
                                    </div>
                                </th>
                                <th className="total-column">
                                    <div className="club-header">
                                        <div className="club-name-wrapper">
                                            <span className="club-name" title="Total">
                                                {truncateClubName("Total")}
                                            </span>
                                        </div>
                                    </div>
                                </th>
                                <th className="remaining-column">
                                    <div className="club-header">
                                        <div className="club-name-wrapper">
                                            <span className="club-name" title="Remaining">
                                                {truncateClubName("Remaining")}
                                            </span>
                                        </div>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {teeSizes.map((size, sizeIndex) => (
                                <tr key={sizeIndex} className={sizeIndex % 2 === 0 ? 'even-row' : 'odd-row'}>
                                    <td 
                                        className={`sticky-column size-column ${getHighlightClass(sizeIndex, 0)}`}
                                        onMouseEnter={() => handleCellHover(sizeIndex, 0)}
                                        onMouseLeave={handleCellLeave}
                                    >
                                        <strong>{size}</strong>
                                    </td>
                                    {clubs.map((club, clubIndex) => (
                                        <td 
                                            key={clubIndex} 
                                            className={`club-cell ${getHighlightClass(sizeIndex, clubIndex + 1)}`}
                                            onMouseEnter={() => handleCellHover(sizeIndex, clubIndex + 1)}
                                            onMouseLeave={handleCellLeave}
                                        >
                                            {distribution[size][club] > 0 ? (
                                                <Badge color="info" className="count-badge">
                                                    {distribution[size][club]}
                                                </Badge>
                                            ) : (
                                                <span className="zero-count">-</span>
                                            )}
                                        </td>
                                    ))}
                                    <td 
                                        className={`no-club-cell ${getHighlightClass(sizeIndex, clubs.length + 1)}`}
                                        onMouseEnter={() => handleCellHover(sizeIndex, clubs.length + 1)}
                                        onMouseLeave={handleCellLeave}
                                    >
                                        {distribution[size]['No Club'] > 0 ? (
                                            <Badge color="warning" className="count-badge">
                                                {distribution[size]['No Club']}
                                            </Badge>
                                        ) : (
                                            <span className="zero-count">-</span>
                                        )}
                                    </td>
                                    <td 
                                        className={`total-cell ${getHighlightClass(sizeIndex, clubs.length + 2)}`}
                                        onMouseEnter={() => handleCellHover(sizeIndex, clubs.length + 2)}
                                        onMouseLeave={handleCellLeave}
                                    >
                                        <Badge color="primary" className="count-badge">
                                            {distribution[size]['Total']}
                                        </Badge>
                                    </td>
                                    <td 
                                        className={`remaining-cell ${getHighlightClass(sizeIndex, clubs.length + 3)}`}
                                        onMouseEnter={() => handleCellHover(sizeIndex, clubs.length + 3)}
                                        onMouseLeave={handleCellLeave}
                                    >
                                        {distribution[size]['Remaining'] > 0 ? (
                                            <Badge color="success" className="count-badge">
                                                {distribution[size]['Remaining']}
                                            </Badge>
                                        ) : (
                                            <Badge color="danger" className="count-badge">
                                                {distribution[size]['Remaining']}
                                            </Badge>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            
                            {/* Totals Row */}
                            <tr className="totals-row">
                                <td 
                                    className={`sticky-column size-column ${getHighlightClass(teeSizes.length, 0)}`}
                                    onMouseEnter={() => handleCellHover(teeSizes.length, 0)}
                                    onMouseLeave={handleCellLeave}
                                >
                                    <strong>TOTALS</strong>
                                </td>
                                {clubs.map((club, clubIndex) => (
                                    <td 
                                        key={clubIndex} 
                                        className={`club-cell totals-cell ${getHighlightClass(teeSizes.length, clubIndex + 1)}`}
                                        onMouseEnter={() => handleCellHover(teeSizes.length, clubIndex + 1)}
                                        onMouseLeave={handleCellLeave}
                                    >
                                        {distribution['TOTALS'][club] > 0 ? (
                                            <Badge color="dark" className="count-badge">
                                                {distribution['TOTALS'][club]}
                                            </Badge>
                                        ) : (
                                            <span className="zero-count">-</span>
                                        )}
                                    </td>
                                ))}
                                <td 
                                    className={`no-club-cell totals-cell ${getHighlightClass(teeSizes.length, clubs.length + 1)}`}
                                    onMouseEnter={() => handleCellHover(teeSizes.length, clubs.length + 1)}
                                    onMouseLeave={handleCellLeave}
                                >
                                    {distribution['TOTALS']['No Club'] > 0 ? (
                                        <Badge color="dark" className="count-badge">
                                            {distribution['TOTALS']['No Club']}
                                        </Badge>
                                    ) : (
                                        <span className="zero-count">-</span>
                                    )}
                                </td>
                                <td 
                                    className={`total-cell totals-cell ${getHighlightClass(teeSizes.length, clubs.length + 2)}`}
                                    onMouseEnter={() => handleCellHover(teeSizes.length, clubs.length + 2)}
                                    onMouseLeave={handleCellLeave}
                                >
                                    <Badge color="dark" className="count-badge">
                                        {distribution['TOTALS']['Total']}
                                    </Badge>
                                </td>
                                <td 
                                    className={`remaining-cell totals-cell ${getHighlightClass(teeSizes.length, clubs.length + 3)}`}
                                    onMouseEnter={() => handleCellHover(teeSizes.length, clubs.length + 3)}
                                    onMouseLeave={handleCellLeave}
                                >
                                    {distribution['TOTALS']['Remaining'] > 0 ? (
                                        <Badge color="success" className="count-badge">
                                            {distribution['TOTALS']['Remaining']}
                                        </Badge>
                                    ) : (
                                        <Badge color="danger" className="count-badge">
                                            {distribution['TOTALS']['Remaining']}
                                        </Badge>
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
                
                <div className="table-legend mt-3">
                    <div className="row">
                        <div className="col-md-6">
                            <h6>Legend:</h6>
                            <div className="d-flex flex-wrap gap-2">
                                <Badge color="info">Club Counts</Badge>
                                <Badge color="warning">No Club</Badge>
                                <Badge color="primary">Total Assigned</Badge>
                                <Badge color="success">Remaining Stock</Badge>
                                <Badge color="danger">Out of Stock</Badge>
                            </div>
                        </div>
                        <div className="col-md-6 text-end">
                            <small className="text-muted">
                                Scroll horizontally to view all clubs • {clubs.length} clubs found
                            </small>
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default RaceTeeDistributionTable;
